import decompress from 'brotli/decompress';

// AGWTracer's storage layer wraps every message body with a brotli layer
// (`brotli_` prefix + base64). When that wrapper is stripped, we may be
// left with another encoding INSIDE — most commonly the original
// HTTP-wire `Content-Encoding: gzip` body that the sidecar captured
// verbatim from upstream. UTF-8 decoding gzipped bytes produces 213k+
// U+FFFD replacement characters per response (every byte ≥ 0x80 is
// invalid UTF-8) and shows up in the UI / ZIP downloads as garbled
// text — exactly the symptom reported on sandbox 2026-05-01 for
// gzipped AirShopping responses.
//
// This helper handles both the storage wrapper AND the inner
// content-encoding so callers don't have to think about it.

const STORAGE_BROTLI_PREFIX = 'brotli_';
const GZIP_MAGIC = [0x1f, 0x8b];

/**
 * Decode a stored message body string into the displayable plain-text
 * representation. The string format we receive is whatever the backend
 * returned: most commonly `brotli_<base64>` (compressed wrapper), but
 * may also be a bare string from very old rows.
 *
 * Returns the UTF-8 string suitable for display, syntax-highlighting,
 * or ZIP-export. If the inner payload was gzipped (typical for
 * provider responses), it is gunzipped first so the user sees the
 * original JSON / XML rather than the binary gzip stream.
 *
 * Falls back to plain UTF-8 decoding when the buffer is not gzipped,
 * preserving behaviour for older message rows.
 */
export async function decodeMessageBody(stored: string): Promise<string> {
  if (!stored) return '';

  let payload: Uint8Array;
  if (stored.startsWith(STORAGE_BROTLI_PREFIX)) {
    const compressed = stored.slice(STORAGE_BROTLI_PREFIX.length);
    const compressedBuffer = Buffer.from(compressed, 'base64');
    payload = decompress(compressedBuffer);
  } else {
    // Pre-storage-wrapper rows: the body is the literal string. Encode
    // back to bytes so the gzip-magic check below treats it uniformly.
    payload = new TextEncoder().encode(stored);
  }

  if (isGzipped(payload)) {
    const ds = new DecompressionStream('gzip');
    const ungzipped = await new Response(
      new Blob([payload]).stream().pipeThrough(ds)
    ).arrayBuffer();
    return new TextDecoder('utf-8').decode(ungzipped);
  }

  return new TextDecoder('utf-8').decode(payload);
}

function isGzipped(buf: Uint8Array): boolean {
  return buf.length >= 2 && buf[0] === GZIP_MAGIC[0] && buf[1] === GZIP_MAGIC[1];
}
